import React from "react"
import { Alert, Col, Drawer, Input, notification, Row, Select, Space, Spin, Switch, Tabs } from "antd"
import api from "src/api"
import { Button } from "src/components/actions/button"
import { CloseSvg, SaveSvg } from "src/components/Svg"
import { FeedUrlsEditor } from "src/pages-lib/points/feed-urls-editor"
import { useAppData } from "src/store/app-data"
import styled from "styled-components"

const PLATFORMS = [
  { label: "2ГИС", value: "2gis" },
  { label: "Яндекс", value: "yandex" },
]

const FEATURE_TYPE_TITLES = {
  boolean: "Да / нет",
  enum_single: "Одно значение из списка",
  enum_multiple: "Несколько значений из списка",
  numeric_single: "Число",
  range_single: "Диапазон",
  range_in_units_single: "Диапазон с единицами",
}

/**
 * Признаки и ссылки филиала для внешних фидов.
 *
 * Наборы у площадок свои, поэтому всё внутри переключается вкладкой площадки.
 * Ссылки точки перекрывают набор её типа целиком: пустой список означает,
 * что в фид уйдут ссылки бренда.
 */
export function FeedDrawer({ point, onClose }) {
  const layoutConfig = useAppData((state) => state.layoutConfig)
  const [platform, setPlatform] = React.useState(PLATFORMS[0].value)
  const [isLoading, setIsLoading] = React.useState(false)
  const [isSaving, setIsSaving] = React.useState(false)
  const [features, setFeatures] = React.useState([])
  const [search, setSearch] = React.useState("")

  React.useEffect(() => {
    if (point) {
      loadData()
    }
  }, [point?.id, platform])

  async function loadData() {
    setIsLoading(true)
    try {
      const response = await api.get(`points/${point.id}/feed-features?platform=${platform}`)

      setFeatures(response.data.items)
    } catch (error) {
      console.error("ошибка загрузки признаков", error)
      notification.error({ message: "Не удалось загрузить признаки" })
    } finally {
      setIsLoading(false)
    }
  }

  function onChangeFeature(id, patch) {
    setFeatures((items) =>
      items.map((item) => (item.id === id ? { ...item, ...patch, isSet: true } : item))
    )
  }

  function onResetFeature(id) {
    setFeatures((items) =>
      items.map((item) =>
        item.id === id
          ? { ...item, isSet: false, value: null, values: [], from: null, to: null }
          : item
      )
    )
  }

  async function onSaveFeatures() {
    setIsSaving(true)
    try {
      const payload = features
        .filter((feature) => feature.isSet)
        .map((feature) => ({
          id: feature.id,
          value: feature.value ?? null,
          values: feature.values ?? [],
          from: feature.from ?? null,
          to: feature.to ?? null,
        }))

      await api.put(`points/${point.id}/feed-features`, { platform, features: payload })
      notification.success({ message: "Признаки сохранены" })
    } catch (error) {
      console.error("ошибка сохранения признаков", error)
      notification.error({ message: "Ошибка сохранения признаков" })
    } finally {
      setIsSaving(false)
    }
  }

  const query = search.trim().toLowerCase()
  const visibleFeatures = query
    ? features.filter(
      (feature) =>
        feature.name?.toLowerCase().includes(query) ||
          feature.feedName?.toLowerCase().includes(query)
    )
    : features
  const selectedCount = features.filter((feature) => feature.isSet).length

  const featureTabs = (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Признаки без галочки в фид не уходят. Если у точки не задан ни один признак, площадка получит общий набор сети. Значения перечислений можно выбрать из списка площадки или ввести своё."
      />
      <StyledFeatureHeader>
        <Input.Search
          allowClear
          placeholder="Поиск по названию признака"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          style={{ maxWidth: 320 }}
        />
        <span>
          Отмечено: {selectedCount} из {features.length}
        </span>
      </StyledFeatureHeader>
      {visibleFeatures.map((feature) => (
        <StyledFeatureRow gutter={16} align="middle" key={feature.id}>
          <Col span={9}>
            <StyledFeatureName>{feature.name}</StyledFeatureName>
            <StyledFeatureType>
              {feature.feedName} · {FEATURE_TYPE_TITLES[feature.feedType] ?? feature.feedType}
              {feature.feedUnitValue ? ` · ${feature.feedUnitValue}` : ""}
            </StyledFeatureType>
          </Col>
          <Col span={4}>
            <Switch
              checked={feature.isSet}
              checkedChildren="В фиде"
              unCheckedChildren="Нет"
              onChange={(checked) =>
                checked ? onChangeFeature(feature.id, {}) : onResetFeature(feature.id)
              }
            />
          </Col>
          <Col span={11}>{renderFeatureValue(feature, onChangeFeature)}</Col>
        </StyledFeatureRow>
      ))}
      {visibleFeatures.length === 0 && <StyledEmpty>Признаки не найдены</StyledEmpty>}
    </>
  )

  return (
    <StyledDrawer
      width={layoutConfig.isMobile ? "90%" : "60%"}
      title={`Фиды точки: ${point?.nameRu ?? ""}`}
      onClose={onClose}
      open
      extra={
        <Space>
          <Select
            value={platform}
            options={PLATFORMS}
            onChange={setPlatform}
            style={{ width: 140 }}
          />
          <Button type="text" onClick={onClose}>
            <CloseSvg /> Закрыть
          </Button>
        </Space>
      }
    >
      <Spin spinning={isLoading}>
        <Tabs
          defaultActiveKey="urls"
          items={[
            {
              key: "urls",
              label: "Ссылки",
              children: (
                <FeedUrlsEditor
                  endpoint={`points/${point.id}/feed-urls`}
                  platform={platform}
                  hint="Пустой список означает, что в фид уйдут ссылки типа точки (раздел «Типы точек»). Порядок строк — порядок тегов в фиде."
                />
              ),
            },
            {
              key: "features",
              label: "Признаки",
              children: (
                <>
                  {featureTabs}
                  <StyledFooter>
                    <Button type="primary" loading={isSaving} onClick={onSaveFeatures}>
                      <SaveSvg /> Сохранить признаки
                    </Button>
                  </StyledFooter>
                </>
              ),
            },
          ]}
        />
      </Spin>
    </StyledDrawer>
  )
}

/**
 * Поля значения зависят от типа признака: одиночное значение, список значений
 * либо диапазон.
 */
function renderFeatureValue(feature, onChange) {
  if (!feature.isSet) {
    return null
  }

  if (feature.feedType === "boolean") {
    return (
      <Select
        value={feature.value ?? "0"}
        style={{ width: "100%" }}
        options={[
          { label: "Да (1)", value: "1" },
          { label: "Нет (0)", value: "0" },
        ]}
        onChange={(value) => onChange(feature.id, { value })}
      />
    )
  }

  if (feature.feedType === "enum_multiple") {
    return (
      <Select
        mode="tags"
        value={feature.values ?? []}
        style={{ width: "100%" }}
        placeholder="Выберите значения"
        options={toOptions(feature)}
        optionFilterProp="label"
        onChange={(values) => onChange(feature.id, { values })}
      />
    )
  }

  if (feature.feedType === "enum_single") {
    return (
      <Select
        allowClear
        showSearch
        value={feature.value ?? undefined}
        style={{ width: "100%" }}
        placeholder="Выберите значение"
        options={toOptions(feature)}
        optionFilterProp="label"
        onChange={(value) => onChange(feature.id, { value: value ?? null })}
      />
    )
  }

  if (feature.feedType === "range_single" || feature.feedType === "range_in_units_single") {
    return (
      <Row gutter={8}>
        <Col span={12}>
          <Input
            value={feature.from ?? ""}
            placeholder="от"
            onChange={(event) => onChange(feature.id, { from: event.target.value })}
          />
        </Col>
        <Col span={12}>
          <Input
            value={feature.to ?? ""}
            placeholder="до"
            onChange={(event) => onChange(feature.id, { to: event.target.value })}
          />
        </Col>
      </Row>
    )
  }

  return (
    <Input
      value={feature.value ?? ""}
      placeholder="значение"
      onChange={(event) => onChange(feature.id, { value: event.target.value })}
    />
  )
}

/**
 * Значения перечисления из справочника площадки. Список подсказывает, что
 * понимает площадка, но ввод им не ограничен: справочник ведётся вручную.
 *
 * Если у признака есть список из документации, значения делятся на две группы:
 * описанные в рекомендациях и те, что площадка принимает по факту — так видно,
 * какое значение безопаснее выбрать.
 */
function toOptions(feature) {
  const options = feature.options ?? []
  const toItem = (option) => ({
    label: `${option.title} (${option.value})`,
    value: option.value,
  })

  const hasDocumented = options.some((option) => option.documented !== null)
  if (!hasDocumented) {
    return options.map(toItem)
  }

  const documented = options.filter((option) => option.documented).map(toItem)
  const undocumented = options.filter((option) => !option.documented).map(toItem)

  return [
    { label: "Из документации площадки", options: documented },
    { label: "Принимаются по факту, в документации нет", options: undocumented },
  ].filter((group) => group.options.length > 0)
}

const StyledFeatureHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  color: #8c8c8c;
`

const StyledEmpty = styled.div`
  padding: 24px 0;
  text-align: center;
  color: #8c8c8c;
`

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding-top: 12px;
  }
`

const StyledFeatureRow = styled(Row)`
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
`

const StyledFeatureName = styled.div`
  font-weight: 600;
`

const StyledFeatureType = styled.div`
  font-size: 12px;
  color: #8c8c8c;
`

const StyledFooter = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
`
